/**
 * Browser-Compatible JWT Authentication Service
 * Uses crypto-js and browser APIs instead of Node.js dependencies
 */

import CryptoJS from 'crypto-js';
import { v4 as uuidv4 } from 'uuid';

// Configuration
// HARDCODED JWT SECRET - Previously from REACT_APP_JWT_SECRET environment variable
const JWT_SECRET = 'ab3daa6a8337c2c7f41428cc419b558848e3126b6fa3592d42c763c1f8ce27acde850de350b90ebae650765defad764daf243ac3e4e39359e488275a5803915a';
// const JWT_SECRET = process.env.REACT_APP_JWT_SECRET || 'your-default-secret-key-change-in-production'; // COMMENTED OUT - Now hardcoded above
const DEFAULT_TTL = 600; // 10 minutes in seconds
const MAX_TTL = 3600; // 1 hour maximum

// In-memory session store
const activeSessions = new Map();
const revokedTokens = new Set();

/**
 * Browser-compatible JWT implementation using crypto-js
 */
class BrowserJWTService {
  constructor() {
    this.serviceName = 'Browser JWT Authentication Service';
    this.version = '1.0.0';
    this.startTime = Date.now();
    this.cleanupInterval = null;
    this.setupCleanup();
  }

  /**
   * Setup automatic cleanup of expired sessions
   */
  setupCleanup() {
    this.cleanupInterval = setInterval(() => {
      this.cleanupExpiredSessions();
    }, 5 * 60 * 1000); // 5 minutes
  }

  /**
   * Clean up expired sessions
   */
  cleanupExpiredSessions() {
    const now = Math.floor(Date.now() / 1000);
    let cleanedCount = 0;

    for (const [sessionId, session] of activeSessions.entries()) {
      if (session.exp <= now) {
        activeSessions.delete(sessionId);
        revokedTokens.add(session.jti);
        cleanedCount++;
      }
    }

    if (cleanedCount > 0) {
      console.log(`🧹 Cleaned up ${cleanedCount} expired sessions`);
    }

    // Limit revoked tokens set size
    if (revokedTokens.size > 1000) {
      const tokensArray = Array.from(revokedTokens);
      revokedTokens.clear();
      // Keep only the last 500
      tokensArray.slice(-500).forEach(token => revokedTokens.add(token));
    }
  }

  /**
   * Create JWT header
   */
  createJWTHeader() {
    return {
      alg: 'HS256',
      typ: 'JWT'
    };
  }

  /**
   * Base64 URL encode
   */
  base64UrlEncode(str) {
    return btoa(str)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  /**
   * Base64 URL decode
   */
  base64UrlDecode(str) {
    str = str.replace(/-/g, '+').replace(/_/g, '/');
    while (str.length % 4) {
      str += '=';
    }
    return atob(str);
  }

  /**
   * Generate JWT Token
   */
  generateJWTToken(email, slotId, requestor = 'unknown', customTTL = null) {
    try {
      if (!email || !slotId) {
        return {
          success: false,
          error: 'Email and slotId are required',
          code: 'missing_params'
        };
      }

      const sessionId = uuidv4();
      const jwtId = uuidv4();
      const now = Math.floor(Date.now() / 1000);
      const ttl = customTTL || DEFAULT_TTL;
      const expiry = now + Math.min(ttl, MAX_TTL);

      // Create header
      const header = this.createJWTHeader();
      const headerEncoded = this.base64UrlEncode(JSON.stringify(header));

      // Create payload
      const payload = {
        email: email,
        slot: slotId,
        sessionId: sessionId,
        requestor: requestor,
        iat: now,
        exp: expiry,
        jti: jwtId,
        iss: this.serviceName,
        sub: email
      };
      const payloadEncoded = this.base64UrlEncode(JSON.stringify(payload));

      // Create signature
      const signatureInput = headerEncoded + '.' + payloadEncoded;
      const signature = CryptoJS.HmacSHA256(signatureInput, JWT_SECRET)
        .toString(CryptoJS.enc.Base64)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');

      const token = headerEncoded + '.' + payloadEncoded + '.' + signature;

      // Store session
      activeSessions.set(sessionId, {
        sessionId,
        email,
        slotId,
        requestor,
        jti: jwtId,
        token,
        createdAt: new Date().toISOString(),
        expiresAt: new Date(expiry * 1000).toISOString(),
        timeRemaining: ttl,
        active: true,
        exp: expiry,
        iat: now
      });

      return {
        success: true,
        token,
        sessionId,
        jwtId,
        email,
        slotId,
        requestor,
        expiresAt: new Date(expiry * 1000).toISOString(),
        timeRemaining: ttl
      };

    } catch (error) {
      console.error('Error generating JWT token:', error);
      return {
        success: false,
        error: 'Failed to generate JWT token: ' + error.message,
        code: 'generation_failed'
      };
    }
  }

  /**
   * Validate JWT Token
   */
  validateJWTToken(token) {
    try {
      if (!token) {
        return {
          success: false,
          error: 'Token is required',
          code: 'missing_token'
        };
      }

      // Check if token is revoked
      const parts = token.split('.');
      if (parts.length !== 3) {
        return {
          success: false,
          error: 'Invalid token format',
          code: 'invalid_format'
        };
      }

      // Decode payload to get JTI
      const payload = JSON.parse(this.base64UrlDecode(parts[1]));
      
      if (revokedTokens.has(payload.jti)) {
        return {
          success: false,
          error: 'Token has been revoked',
          code: 'token_revoked'
        };
      }

      // Verify signature
      const headerEncoded = parts[0];
      const payloadEncoded = parts[1];
      const signature = parts[2];

      const expectedSignature = CryptoJS.HmacSHA256(headerEncoded + '.' + payloadEncoded, JWT_SECRET)
        .toString(CryptoJS.enc.Base64)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');

      if (signature !== expectedSignature) {
        return {
          success: false,
          error: 'Invalid token signature',
          code: 'invalid_signature'
        };
      }

      // Check expiration
      const now = Math.floor(Date.now() / 1000);
      if (payload.exp < now) {
        return {
          success: false,
          error: 'Token has expired',
          code: 'token_expired'
        };
      }

      return {
        success: true,
        email: payload.email,
        slotId: payload.slot,
        sessionId: payload.sessionId,
        jwtId: payload.jti,
        requestor: payload.requestor,
        expiresAt: new Date(payload.exp * 1000).toISOString(),
        timeRemaining: payload.exp - now
      };

    } catch (error) {
      console.error('Error validating JWT token:', error);
      return {
        success: false,
        error: 'Token validation failed: ' + error.message,
        code: 'validation_failed'
      };
    }
  }

  /**
   * Revoke Token
   */
  revokeToken(token) {
    try {
      // First validate the token to get its JTI
      const validation = this.validateJWTToken(token);
      
      if (!validation.success) {
        return {
          success: false,
          error: 'Cannot revoke invalid token',
          code: 'invalid_token'
        };
      }

      // Add to revoked tokens list
      revokedTokens.add(validation.jwtId);

      // Remove from active sessions
      for (const [sessionId, session] of activeSessions.entries()) {
        if (session.jti === validation.jwtId) {
          session.active = false;
          activeSessions.delete(sessionId);
          break;
        }
      }

      return {
        success: true,
        message: 'Token revoked successfully'
      };

    } catch (error) {
      console.error('Error revoking token:', error);
      return {
        success: false,
        error: 'Failed to revoke token: ' + error.message,
        code: 'revocation_failed'
      };
    }
  }

  /**
   * Get Active Sessions
   */
  getActiveSessions() {
    try {
      // Clean up expired sessions first
      this.cleanupExpiredSessions();

      const sessions = [];
      for (const session of activeSessions.values()) {
        if (session.active) {
          sessions.push({
            sessionId: session.sessionId,
            email: session.email,
            slotId: session.slotId,
            requestor: session.requestor,
            createdAt: session.createdAt,
            expiresAt: session.expiresAt,
            timeRemaining: session.timeRemaining,
            token: session.token
          });
        }
      }

      return {
        totalActive: sessions.length,
        totalRevoked: revokedTokens.size,
        details: sessions
      };

    } catch (error) {
      console.error('Error getting active sessions:', error);
      return {
        totalActive: 0,
        totalRevoked: 0,
        details: []
      };
    }
  }

  /**
   * Check if token is expiring soon
   */
  isTokenExpiringSoon(token, thresholdSeconds = 120) {
    try {
      const validation = this.validateJWTToken(token);
      
      if (!validation.success) {
        return { isExpiring: false, timeRemaining: 0, expired: true };
      }
      
      const isExpiring = validation.timeRemaining <= thresholdSeconds;
      
      return {
        isExpiring,
        timeRemaining: validation.timeRemaining,
        expired: false,
        formattedTime: this.formatTimeRemaining(validation.timeRemaining)
      };
    } catch (error) {
      return { isExpiring: false, timeRemaining: 0, expired: true };
    }
  }

  /**
   * Format time remaining for display
   */
  formatTimeRemaining(seconds) {
    if (seconds <= 0) return '0s';
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${remainingSeconds}s`;
    }
  }

  /**
   * Get Health Status
   */
  getHealthStatus() {
    const uptime = Math.floor((Date.now() - this.startTime) / 1000);
    return {
      serviceName: this.serviceName,
      version: this.version,
      defaultTtl: DEFAULT_TTL,
      maxTtl: MAX_TTL,
      uptime: `${uptime}s`,
      activeSessionsCount: activeSessions.size,
      revokedTokensCount: revokedTokens.size
    };
  }

  /**
   * Cleanup on service destruction
   */
  destroy() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }
  }
}

// Export the class as default
export default BrowserJWTService;

// Also create and export a singleton instance for convenience
export const browserJWTService = new BrowserJWTService();
